import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const userRole = localStorage.getItem('role');

  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * OAuthCallbackRedirect - catches Google's OAuth2 redirect to /api/v1/admin/meetings/oauth2/callback
 * Since Hostinger serves static files, this React route intercepts the callback,
 * extracts code/error from query params, and forwards them to the dashboard OAuth2 page.
 */
const OAuthCallbackRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const code = params.get('code');
  const error = params.get('error');

  const redirectParams = new URLSearchParams();
  if (code) redirectParams.set('code', code);
  if (error) redirectParams.set('error', error);

  const redirectTo = `/dashboard/google-oauth2${redirectParams.toString() ? '?' + redirectParams.toString() : ''}`;
  return <Navigate to={redirectTo} replace />;
};
import LoginPage from './pages/LoginPage'
import DashboardLayout from './pages/DashboardLayout'
import Dashboard from './pages/Dashboard'
import UsersPage from './pages/UsersPage'
import SubscribersPage from './pages/SubscribersPage'
import OrdersPage from './pages/OrdersPage'
import MasterDataPage from './pages/MasterDataPage'
import MasterDataRulesPage from './pages/MasterDataRulesPage'
import DietChartsPage from './pages/DietChartsPage'
import DietCombinationsPage from './pages/DietCombinationsPage'
import FoodMacrosPage from './pages/FoodMacrosPage'
import BannerPage from './pages/BannerPage'
import TrainerPage from './pages/TrainerPage'
import TestimonialPage from './pages/TestimonialPage'
import UserTestimonialPage from './pages/UserTestimonialPage'
import GlobalVideoPage from './pages/GlobalVideoPage'
import AddonSessionsPage from './pages/AddonSessionsPage'
import AddonPlansPage from './pages/AddonPlansPage'
import SlotManagePage from './pages/SlotManagePage'
import SubscriptionsPage from './pages/SubscriptionsPage'
import TrainerTimeSlotPage from './pages/TrainerTimeSlotPage'
import ApprovalRequestsPage from './pages/ApprovalRequestsPage'
import BookedSessionsPage from './pages/BookedSessionsPage'
import GoogleOAuth2Page from './pages/GoogleOAuth2Page'
import SettingsPage from './pages/SettingsPage'
import HelpSupportPage from './pages/HelpSupportPage'
import AdminManagementPage from './pages/AdminManagementPage'
import SessionExpiredModal from './components/SessionExpiredModal'

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false)
  const [sessionModalType, setSessionModalType] = useState('session-expired')

  const updateAuth = () => {
    const userId = localStorage.getItem('userId')
    setIsAuthenticated(!!userId)
  }

  useEffect(() => {
    updateAuth()
    setLoading(false)

    const handler = () => updateAuth()
    const tokenExpiredHandler = () => setShowSessionExpiredModal(true)

    window.addEventListener('storage', handler)
    window.addEventListener('auth-changed', handler)
    window.addEventListener('tokenExpired', tokenExpiredHandler)

    return () => {
      window.removeEventListener('storage', handler)
      window.removeEventListener('auth-changed', handler)
      window.removeEventListener('tokenExpired', tokenExpiredHandler)
    }
  }, [])

  const handleSessionExpired = () => {
    // Clear all authentication data
    localStorage.removeItem('userId')
    localStorage.removeItem('username')
    localStorage.removeItem('email')
    localStorage.removeItem('firstName')
    localStorage.removeItem('lastName')
    localStorage.removeItem('role')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('tokenType')

    // Update authentication state
    setIsAuthenticated(false)
    setShowSessionExpiredModal(false)
    setSessionModalType('session-expired')
  }

  useEffect(() => {
    const handleUnauthorizedAccess = (event) => {
      setSessionModalType('unauthorized');
      setShowSessionExpiredModal(true);
    };

    window.addEventListener('unauthorizedAccess', handleUnauthorizedAccess);

    return () => {
      window.removeEventListener('unauthorizedAccess', handleUnauthorizedAccess);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    )
  }

  return (
    <>
      <SessionExpiredModal
        isOpen={showSessionExpiredModal}
        onConfirm={handleSessionExpired}
        modalType={sessionModalType}
        message={sessionModalType === 'unauthorized' ? 'Access denied: Insufficient permissions' : 'Your session has expired. Please log in again to continue using the application.'}
      />
      <Router>
        <Routes>
          {/* Google OAuth2 callback route - catches Google's redirect before React Router 404s it */}
          <Route path="/api/v1/admin/meetings/oauth2/callback" element={<OAuthCallbackRedirect />} />
          <Route
            path="/"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
            }
          />
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? <DashboardLayout /> : <Navigate to="/" replace />
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="subscribers" element={<SubscribersPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="masterdata" element={<MasterDataPage />} />
            <Route path="masterdatarules" element={<MasterDataRulesPage />} />
            <Route path="dietcharts" element={<DietChartsPage />} />
            <Route path="diet-combinations" element={<DietCombinationsPage />} />
            <Route path="food-macros" element={<FoodMacrosPage />} />
            <Route path="banner" element={<BannerPage />} />
            <Route path="trainer" element={<TrainerPage />} />
            <Route path="testomonial" element={<TestimonialPage />} />
            <Route path="user-testimonials" element={<UserTestimonialPage />} />
            <Route path="globalvideo" element={<GlobalVideoPage />} />
            <Route path="addonsessions" element={<AddonSessionsPage />} />
            <Route path="addonplans" element={<AddonPlansPage />} />
            {/* <Route path="slotmanage" element={<SlotManagePage />} /> */}
            <Route path="trainer-time-slots" element={<TrainerTimeSlotPage />} />
            <Route path="approval-requests" element={<ApprovalRequestsPage />} />
            <Route path="booked-sessions" element={<BookedSessionsPage />} />
            <Route path="google-oauth2" element={<GoogleOAuth2Page />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="help-support" element={<HelpSupportPage />} />
            <Route path="admin-management" element={<ProtectedRoute requiredRole="SUPER_ADMIN"><AdminManagementPage /></ProtectedRoute>} />
          </Route>
        </Routes>
      </Router>
    </>
  )
}

export default App
